import React from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  hue?: number;
}

export function Modal({ isOpen, onClose, title, children, footer, className, hue }: ModalProps) {
  if (!isOpen) return null;

  // Calculate effective hue by adding the rotation to the base green hue (101)
  const effectiveHue = hue !== undefined ? (101 + hue) % 360 : undefined;

  return createPortal(
    <div 
      className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      style={{ 
        '--hue': effectiveHue !== undefined ? `${effectiveHue}deg` : undefined,
        filter: hue && hue !== 0 ? `hue-rotate(${hue}deg)` : 'none'
      } as React.CSSProperties}
    >
      <div className={`w-full max-w-md bg-neutral-900 border border-white/10 rounded-lg shadow-2xl flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200 ${className || ''}`}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="font-semibold text-white">{title}</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0 text-white">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 text-sm text-slate-700 dark:text-slate-300">
          {children}
        </div>
        {footer && (
          <div className="p-4 border-t border-white/10 bg-black/5 dark:bg-black/20">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
